import { Api } from '@/api/api.client';
import { Components } from '@/api/generated/api';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirmDialog } from './useConfirmDialog';
import { reportGroupKeys } from './reportGroup/reportGroup.keys';
import { reportKeys } from './report/report.keys';

interface IReportGroupComposable {
  allReportGroups: ComputedRef<Components.Schemas.ReportGroupDto[]>;
  assignReportToGroup: (groupId: number, reportId: number) => Promise<void>;
  createReportGroup: (
    classId: number,
    dto: Components.Schemas.CreateReportGroupDto,
  ) => Promise<Components.Schemas.ReportGroupDto>;
  deleteReportGroup: (groupId: number) => Promise<void>;
  refresh: () => Promise<void>;
  unassignReportFromGroup: (report: Components.Schemas.BaseReportDto) => Promise<void>;
}

export const useReportGroup = (): IReportGroupComposable => {
  const { createSuccessToast, catchError, deleteSuccessToast, editSuccessToast } = useToast();
  const { currentClass } = useCurrentClass();
  const { confirm } = useConfirmDialog();
  const { t } = useI18n();

  const getReportGroups = async (): Promise<Components.Schemas.ReportGroupDto[]> => {
    if (!currentClass.value) return [];
    const result = await Api.Routes.ReportGroup.getGroups({ schoolClassId: currentClass.value.id });
    return result.rows;
  };
  const reportGroupListQueryKey = computed(() => reportGroupKeys.classList(currentClass.value?.id));
  const queryClient = useQueryClient();
  const { data } = useQueryWithToast({
    queryKey: reportGroupListQueryKey,
    queryFn: getReportGroups,
  });

  const allReportGroups = computed<Components.Schemas.ReportGroupDto[]>(() => {
    return data.value ?? [];
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: reportGroupListQueryKey.value });
  };

  // A moved report changes the group counts and the report lists alike.
  const refreshAfterReportMove = async (): Promise<void> => {
    await Promise.all([
      refresh(),
      queryClient.invalidateQueries({ queryKey: reportKeys.classLists(currentClass.value?.id) }),
    ]);
  };

  const createReportGroup = async (
    classId: number,
    dto: Components.Schemas.CreateReportGroupDto,
  ): Promise<Components.Schemas.ReportGroupDto> => {
    const result = await catchError(async () => {
      const created = await Api.Routes.ReportGroup.createGroup({ schoolClassId: classId }, dto);
      await refresh();
      createSuccessToast();
      return created.data;
    });
    return result;
  };

  const assignReportToGroup = async (groupId: number, reportId: number): Promise<void> => {
    await catchError(async () => {
      if (!currentClass.value) return;
      const dto: Components.Schemas.AssignReportsToGroupDto = {
        reportIds: [reportId],
      };
      await Api.Routes.ReportGroup.assignReports({ schoolClassId: currentClass.value.id, groupId }, dto);
      await refreshAfterReportMove();
      editSuccessToast();
    });
  };

  const unassignReportFromGroup = async (report: Components.Schemas.BaseReportDto): Promise<void> => {
    await catchError(async () => {
      if (!currentClass.value || !report.groupId) return;

      await Api.Routes.ReportGroup.unassignReport({
        schoolClassId: currentClass.value.id,
        groupId: report.groupId,
        reportId: report.id,
      });
      await refreshAfterReportMove();
      editSuccessToast();
    });
  };

  const deleteReportGroup = async (groupId: number): Promise<void> => {
    const confirmed = await confirm({
      title: t('reports.deleteGroup'),
      description: t('reports.removeGroupConfirmText'),
      confirmButtonLabel: t('general.delete'),
      declineButtonLabel: t('general.cancel'),
      theme: 'danger',
    });

    if (!confirmed) return;

    await catchError(async () => {
      await Api.Routes.ReportGroup.deleteGroup({ groupId });
      await refresh();
      deleteSuccessToast();
    });
  };

  return {
    allReportGroups,
    assignReportToGroup,
    createReportGroup,
    deleteReportGroup,
    refresh,
    unassignReportFromGroup,
  };
};
